import { useState, useEffect, useCallback } from 'react';
import { GameStatistics } from '../types/game';
import { StockCategoryFilter } from '../types/stockCategories';
import { useAuth } from './useAuth';
import { supabase } from '../config/supabase';

interface UseUserStatisticsOptions {
  category?: StockCategoryFilter;
  userId?: string;
  autoFetch?: boolean;
}

interface UseUserStatisticsReturn {
  statistics: GameStatistics | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const EMPTY_STATISTICS: GameStatistics = {
  gamesPlayed: 0,
  winPercentage: 0,
  currentStreak: 0,
  maxStreak: 0,
  guessDistribution: [0, 0, 0, 0, 0, 0]
};

export const useUserStatistics = (options: UseUserStatisticsOptions): UseUserStatisticsReturn => {
  const { category, userId, autoFetch = true } = options;
  const { user } = useAuth();
  const [statistics, setStatistics] = useState<GameStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatisticsFromAPI = useCallback(async (fetchUserId: string, fetchCategory: StockCategoryFilter) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

      // User auth only: never send the admin API key from the browser
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/users/${fetchUserId}/stats/${fetchCategory}`,
        { headers }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // No stats found for this category - return empty stats
          return EMPTY_STATISTICS;
        }
        throw new Error(`Failed to fetch statistics: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error('Invalid response format from statistics API');
      }

      return data.data.statistics as GameStatistics;
    } catch (err) {
      console.error('Error fetching statistics from API:', err);
      throw err;
    }
  }, []);

  const fetchStatistics = useCallback(async () => {
    if (!category) {
      setStatistics(EMPTY_STATISTICS);
      setError(null);
      setIsLoading(false);
      return;
    }

    const targetUserId = userId ?? user?.id;

    if (!targetUserId) {
      setStatistics(null);
      setError('Authentication required to access statistics');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const stats = await fetchStatisticsFromAPI(targetUserId, category);
      setStatistics(stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load statistics';
      setError(errorMessage);
      setStatistics(null);
    } finally {
      setIsLoading(false);
    }
  }, [category, user, userId, fetchStatisticsFromAPI]);

  useEffect(() => {
    if (autoFetch) {
      fetchStatistics();
    }
  }, [autoFetch, fetchStatistics]);

  return {
    statistics,
    isLoading,
    error,
    refetch: fetchStatistics
  };
};
